import type { CSSProperties } from 'react'

/**
 * Speaker colour assignment.
 *
 * A speaker's colour must be stable for the whole meeting and identical in the
 * transcript, the timeline and the summary — a reader uses colour to follow a
 * person, so a re-shuffle on re-render is a correctness bug, not a cosmetic
 * one. So: hash the name, never use array position.
 *
 * An unattributed label ("Speaker 0", or a null name) always gets the grey
 * unknown token. Giving it a hue would make a guess look like a fact.
 */
const PALETTE_SIZE = 8

export function speakerColorVar(speakerName: string | null | undefined): string {
  if (!speakerName || /^speaker\s*\d+$/i.test(speakerName)) return '--sb-speaker-unknown'
  let hash = 0
  for (let i = 0; i < speakerName.length; i += 1) {
    hash = (hash * 31 + speakerName.charCodeAt(i)) % 100_003
  }
  return `--sb-speaker-${(hash % PALETTE_SIZE) + 1}`
}

/** Inline style helper — token colours cannot be expressed as a Tailwind class. */
export function speakerStyle(speakerName: string | null | undefined) {
  const v = speakerColorVar(speakerName)
  return {
    '--speaker': `var(${v})`,
    color: `hsl(var(${v}))`,
  } as CSSProperties
}

export function isUnattributed(speakerName: string | null | undefined): boolean {
  return !speakerName || /^speaker\s*\d+$/i.test(speakerName)
}
